import { Timeout } from '@/types';
import { supabase } from './client';

// Get active timeout for a match (if any)
export async function getActiveTimeout(matchId: string): Promise<Timeout | null> {
  const { data, error } = await supabase
    .from('timeouts')
    .select('*')
    .eq('match_id', matchId)
    .is('ended_at', null)
    .order('started_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    id: data.id,
    matchId: data.match_id,
    setNumber: data.set_number,
    teamId: data.team_id,
    startedAt: data.started_at,
    endedAt: data.ended_at,
    durationSeconds: data.duration_seconds,
  };
}

// Get completed timeouts for a match (for timeout count display)
export async function getCompletedTimeouts(matchId: string): Promise<Timeout[]> {
  const { data, error } = await supabase
    .from('timeouts')
    .select('*')
    .eq('match_id', matchId)
    .not('ended_at', 'is', null)
    .order('started_at', { ascending: true });

  if (error) throw error;
  if (!data) return [];

  return data.map(t => ({
    id: t.id,
    matchId: t.match_id,
    setNumber: t.set_number,
    teamId: t.team_id,
    startedAt: t.started_at,
    endedAt: t.ended_at,
    durationSeconds: t.duration_seconds,
  }));
}

export async function createTimeout(timeoutData: {
  matchId: string;
  setNumber: number;
  teamId: string;
}): Promise<Timeout> {
  const { data, error } = await supabase
    .from('timeouts')
    .insert({
      match_id: timeoutData.matchId,
      set_number: timeoutData.setNumber,
      team_id: timeoutData.teamId,
      started_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw error;

  // Map snake_case DB fields to camelCase for frontend
  return {
    id: data.id,
    matchId: data.match_id,
    setNumber: data.set_number,
    teamId: data.team_id,
    startedAt: data.started_at,
    endedAt: data.ended_at,
    durationSeconds: data.duration_seconds,
  };
}

export async function endTimeout(timeoutId: string): Promise<Timeout> {
  const { data: timeout, error: fetchError } = await supabase
    .from('timeouts')
    .select('started_at')
    .eq('id', timeoutId)
    .single();

  if (fetchError) throw fetchError;

  const endedAt = new Date().toISOString();
  const startedAt = new Date(timeout.started_at).getTime();
  const durationSeconds = Math.floor((new Date(endedAt).getTime() - startedAt) / 1000);

  const { data, error } = await supabase
    .from('timeouts')
    .update({
      ended_at: endedAt,
      duration_seconds: durationSeconds,
    })
    .eq('id', timeoutId)
    .select()
    .single();

  if (error) throw error;

  // Map snake_case DB fields to camelCase for frontend
  return {
    id: data.id,
    matchId: data.match_id,
    setNumber: data.set_number,
    teamId: data.team_id,
    startedAt: data.started_at,
    endedAt: data.ended_at,
    durationSeconds: data.duration_seconds,
  };
}

export async function getTimeoutsByMatch(matchId: string) {
  const { data, error } = await supabase
    .from('timeouts')
    .select('*')
    .eq('match_id', matchId)
    .order('started_at', { ascending: true });

  if (error) throw error;
  return data;
}

export async function deleteTimeout(timeoutId: string) {
  const { error } = await supabase
    .from('timeouts')
    .delete()
    .eq('id', timeoutId);

  if (error) throw error;
}
